/**
 * SessionTimeoutModal
 *
 * Warning modal shown when session is about to expire.
 * Displays countdown timer and options to stay logged in or logout.
 */

import { useEffect, useState } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { Button } from './Button';

interface SessionTimeoutModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Milliseconds remaining until session expires */
  remainingMs: number;
  /** Handler for "Stay Logged In" button */
  onStayLoggedIn: () => Promise<void>;
  /** Handler for "Logout Now" button */
  onLogoutNow: () => Promise<void>;
  /** Handler for clicking outside/dismissing (optional) */
  onDismiss?: () => void;
}

export function SessionTimeoutModal({
  isOpen,
  remainingMs,
  onStayLoggedIn,
  onLogoutNow,
  onDismiss,
}: SessionTimeoutModalProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Format remaining time as mm:ss
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Determine urgency level for styling
  const isUrgent = remainingMs <= 60000; // Less than 1 minute
  const isCritical = remainingMs <= 30000; // Less than 30 seconds

  const handleStayLoggedIn = async () => {
    setIsRefreshing(true);
    try {
      await onStayLoggedIn();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogoutNow = async () => {
    setIsLoggingOut(true);
    try {
      await onLogoutNow();
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onDismiss) {
        onDismiss();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon and Timer */}
        <div className="flex flex-col items-center text-center mb-6">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              isCritical
                ? 'bg-red-100 text-red-600'
                : isUrgent
                ? 'bg-amber-100 text-amber-600'
                : 'bg-blue-100 text-blue-600'
            }`}
          >
            <Clock className="w-8 h-8" />
          </div>

          <h2
            id="session-timeout-title"
            className="text-xl font-semibold text-gray-900 mb-2"
          >
            Session Expiring Soon
          </h2>

          <p className="text-gray-600 mb-4">
            Your session will expire due to inactivity.
          </p>

          {/* Countdown Timer */}
          <div
            className={`text-4xl font-mono font-bold tabular-nums ${
              isCritical
                ? 'text-red-600'
                : isUrgent
                ? 'text-amber-600'
                : 'text-gray-900'
            }`}
          >
            {formattedTime}
          </div>

          <p className="text-sm text-gray-500 mt-1">
            {isCritical
              ? 'Logging out soon...'
              : isUrgent
              ? 'Less than a minute remaining'
              : 'Time remaining'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleStayLoggedIn}
            loading={isRefreshing}
            disabled={isLoggingOut}
            leftIcon={<RefreshCw className="w-4 h-4" />}
            className="w-full"
          >
            Stay Logged In
          </Button>

          <Button
            variant="secondary"
            onClick={handleLogoutNow}
            loading={isLoggingOut}
            disabled={isRefreshing}
            leftIcon={<LogOut className="w-4 h-4" />}
            className="w-full"
          >
            Logout Now
          </Button>
        </div>

        {/* Help text */}
        <p className="text-xs text-gray-400 text-center mt-4">
          Click &quot;Stay Logged In&quot; to continue your session
        </p>
      </div>
    </div>
  );
}
